import { GuaranteeStatus, IReservation, PaymentUtils, Pill, ReservationStatus, TPillTone } from '@localina/core';
import { ClockIcon, DoneIcon, LockIcon } from '@localina/icons';
import { DateTime } from 'luxon';
import React from 'react';
import { useTranslation } from 'react-i18next';

/** whether the reservation shows a guarantee badge (released / refunded / waived guarantees show none) */
export const hasGuaranteeBadge = (reservation: IReservation): boolean =>
    Boolean(reservation.guarantee) &&
    [
        GuaranteeStatus.SECURED,
        GuaranteeStatus.REQUESTED,
        GuaranteeStatus.NOT_REQUESTED,
        GuaranteeStatus.CHARGED,
        GuaranteeStatus.CHARGE_FAILED,
    ].includes(reservation.guarantee!.status);

/**
 * Wide reservation rows give the badge its own column only when a reservation on the page has one (restaurants
 * without No-Show Protection, or a service without guarantees, lose no space). Set per page so that all service
 * groups on it stay aligned; without it a group decides for itself.
 */
export const GuaranteeColumnContext = React.createContext<boolean | undefined>(undefined);

/**
 * Reservation lists: the state of the No-Show Protection guarantee ("🔒 CHF 120", "Waiting for guarantee · 11 h").
 */
export const GuaranteeBadge: React.FC<{ reservation: IReservation }> = ({ reservation }) => {
    const { t, i18n } = useTranslation();
    const guarantee = reservation.guarantee;
    if (!guarantee) {
        return null;
    }
    const amount = PaymentUtils.formatAmount(guarantee.amount, guarantee.currency || 'chf', i18n.language);
    // `short`: shown instead of `label` when the list column is narrow (tablet), see .reservation__guarantee
    const badge = ((): {
        tone: TPillTone;
        label: string;
        short?: string;
        icon?: React.ReactNode;
        title?: string;
    } | null => {
        switch (guarantee.status) {
            case GuaranteeStatus.SECURED:
                return { tone: 'teal', label: amount, icon: <LockIcon /> };
            case GuaranteeStatus.REQUESTED: {
                // widget holds last minutes, link requests hours; past the deadline the expiry job takes over
                const minutes = guarantee.deadline
                    ? Math.ceil(DateTime.fromISO(guarantee.deadline).diffNow('minutes').minutes)
                    : 0;
                const hours = Math.ceil(minutes / 60);
                return {
                    tone: 'warn',
                    icon: <ClockIcon />,
                    label:
                        minutes <= 0
                            ? t('reservations.guarantee.badge.waiting')
                            : minutes < 60
                            ? t('reservations.guarantee.badge.waitingForMinutes', { minutes })
                            : t('reservations.guarantee.badge.waitingFor', { hours }),
                    // narrow: the clock + the time left
                    short:
                        minutes <= 0
                            ? t('reservations.guarantee.badge.waitingShort')
                            : minutes < 60
                            ? t('reservations.guarantee.badge.inMinutes', { minutes })
                            : t('reservations.guarantee.badge.inHours', { hours }),
                };
            }
            case GuaranteeStatus.NOT_REQUESTED:
                // an open request asks for the guarantee once it's accepted
                return reservation.status === ReservationStatus.APPROVAL_PENDING
                    ? {
                          tone: 'neutral',
                          icon: <LockIcon />,
                          label: t('reservations.guarantee.badge.onAcceptance', { amount }),
                          short: amount,
                      }
                    : { tone: 'neutral', label: t('reservations.guarantee.badge.none') };
            case GuaranteeStatus.CHARGED:
                // a dispute needs the restaurant's answer (red); a lost one is only information
                if (guarantee.disputeStatus === 'OPEN') {
                    return {
                        tone: 'critical',
                        label: t('reservations.guarantee.badge.disputed'),
                        short: t('reservations.guarantee.badge.disputedShort'),
                    };
                }
                if (guarantee.disputeStatus === 'LOST') {
                    return {
                        tone: 'warn',
                        label: t('reservations.guarantee.badge.disputeLost'),
                        short: t('reservations.guarantee.badge.disputeLostShort'),
                    };
                }
                // like the secured badge: icon + amount keeps it short in narrow lists ("Charged …" in the tooltip)
                return {
                    tone: 'good',
                    icon: <DoneIcon />,
                    label: amount,
                    title: t('reservations.guarantee.badge.charged', { amount }),
                };
            case GuaranteeStatus.CHARGE_FAILED:
                return {
                    tone: 'critical',
                    label: t('reservations.guarantee.badge.chargeFailed'),
                    short: t('reservations.guarantee.badge.chargeFailedShort'),
                };
            default:
                return null;
        }
    })();
    if (!badge) {
        return null;
    }
    return (
        <Pill
            tone={badge.tone}
            icon={badge.icon}
            label={
                badge.short ? (
                    <>
                        <span className="guarantee-badge__full">{badge.label}</span>
                        <span className="guarantee-badge__short">{badge.short}</span>
                    </>
                ) : (
                    badge.label
                )
            }
            // the full text, also when the column cuts it short
            title={`${t('reservations.guarantee.title')}: ${badge.title ?? badge.label}`}
            extraClassName="guarantee-badge"
        />
    );
};
